import React, { useCallback, useEffect, useState } from "react";
import { AlertOctagon, CheckCircle2, Loader2, RefreshCw, Trash2, X, Archive } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { useConfirm } from "./ConfirmDialog";

/**
 * Phase 101dw.3 — File d'arbitrage des ventes Nayax suspectes.
 *
 * Alimenté automatiquement par le sync Nayax quand une vente déclenche une
 * des heuristiques de suspicion :
 *  - lot_emptied : lot passe à ≤ 0 après décrémention (slot vidé peu avant)
 *  - orphan_slot : aucun lot AM au moment de la sync (slot vide en AM)
 *  - price_mismatch : prix Nayax diverge du prix AM (> 20 %)
 *
 * Chaque vente peut être arbitrée en 3 clics :
 *  - KEEP (vente légitime, garde)
 *  - DELETE (fantôme, supprime + restore stock)
 *  - CANCEL (annule l'impact CA mais garde en historique)
 */
export default function SalesReviewQueuePanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState({});
  const confirm = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sales/review-queue");
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur file d'arbitrage");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const arbitrate = async (sale, action) => {
    const labels = { KEEP: "conserver", DELETE: "supprimer", MARK_CANCELED: "annuler" };
    const descriptions = {
      KEEP: "Confirmer que cette vente est légitime (retire le flag review).",
      DELETE: sale.lot_id
        ? `Supprimer la vente ET restaurer ${sale.quantity} barquette(s) au lot ${sale.lot_number || "?"}.`
        : `Supprimer la vente orpheline (pas de stock à restaurer).`,
      MARK_CANCELED: "Annule l'impact CA (unit_price → 0) mais garde la vente en historique HACCP.",
    };
    if (action !== "KEEP") {
      const ok = await confirm({
        title: `${labels[action][0].toUpperCase() + labels[action].slice(1)} cette vente ?`,
        description: descriptions[action],
        confirmText: labels[action],
      });
      if (!ok) return;
    }
    setProcessing((p) => ({ ...p, [sale.sale_id]: action }));
    try {
      await api.post(`/sales/${sale.sale_id}/review`, { action });
      toast.success(`Vente ${labels[action]}${action === "DELETE" ? ` (${sale.quantity} barquette(s) restaurée(s))` : ""}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur arbitrage");
    } finally {
      setProcessing((p) => { const n = { ...p }; delete n[sale.sale_id]; return n; });
    }
  };

  const reasonLabel = (r) => {
    if (!r) return null;
    const parts = r.split("|");
    return parts.map((p, i) => {
      let label = p, color = "bg-neutral-100 text-neutral-700";
      if (p === "lot_emptied") { label = "Lot vidé"; color = "bg-orange-100 text-orange-800 border-orange-300"; }
      else if (p === "orphan_slot") { label = "Slot vide"; color = "bg-purple-100 text-purple-800 border-purple-300"; }
      else if (p.startsWith("price_mismatch")) { label = "Prix ≠"; color = "bg-red-100 text-red-800 border-red-300"; }
      return <Badge key={i} className={`${color} text-[10px] mr-1`}>{label}</Badge>;
    });
  };

  return (
    <div className="border border-[#E8D3A8] bg-[#FBF4E9] rounded-lg" data-testid="sales-review-panel">
      <div className="p-4 border-b border-[#E8D3A8]">
        <div className="flex items-center gap-2 mb-1">
          <AlertOctagon className="w-5 h-5 text-[#8C6B2F]" />
          <h3 className="font-semibold text-[#8C6B2F]">Ventes Nayax à arbitrer (Phase 101dw.3)</h3>
          <span className="ml-2 text-xs text-[#8C6B2F]">
            {items.length > 0 && `${items.length} en attente`}
          </span>
        </div>
        <p className="text-xs text-[#8C6B2F] leading-relaxed">
          Ventes Nayax auto-flaguées comme suspectes par le sync (lot vidé au moment du tx, aucun stock, ou prix qui diverge).
          Arbitrez chacune en 1 clic : <b>KEEP</b> (légitime), <b>DELETE</b> (fantôme, restore stock), <b>ANNULER</b> (garde en historique mais CA = 0).
        </p>
      </div>

      <div className="p-4 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            disabled={loading}
            data-testid="review-refresh"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
            Actualiser
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="border-[#B24A32] text-[#B24A32] hover:bg-[#FCEEEA]"
            onClick={async () => {
              if (!window.confirm("Purger toutes les alertes « Prix ≠ » sur les distributeurs Corporate (Novelis) ? Les ventes seront gardées (KEEP) sans toucher au stock.")) return;
              try {
                const { data } = await api.post("/sales/review-queue/dismiss-corp-price-mismatch");
                toast.success(`${data.updated} alerte(s) Corporate archivée(s)`);
                await load();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Échec de la purge");
              }
            }}
            data-testid="review-purge-corp-mismatch"
            title="Retire du panneau les alertes prix Novelis (prix subventionné légitime)"
          >
            Purger Prix ≠ Corporate
          </Button>
          <div className="flex-1" />
          <div className="text-xs text-[#8C6B2F]">
            {items.length === 0 && !loading && "✅ Aucune vente à arbitrer"}
          </div>
        </div>

        {items.length === 0 && !loading && (
          <div className="bg-white border border-[#E5E5E0] rounded p-3 text-sm text-[#575A57] flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-[#5A8F76]" />
            Toutes les ventes Nayax sont propres, rien à arbitrer.
          </div>
        )}

        {items.length > 0 && (
          <div className="border border-[#E5E5E0] rounded bg-white overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[#FAFAF7] border-b border-[#E5E5E0]">
                <tr>
                  <th className="text-left px-2 py-2">Date · heure</th>
                  <th className="text-left px-2 py-2">Machine</th>
                  <th className="text-left px-2 py-2">Produit · Lot</th>
                  <th className="text-center px-2 py-2">Qté</th>
                  <th className="text-right px-2 py-2">Prix</th>
                  <th className="text-left px-2 py-2">Motifs</th>
                  <th className="text-right px-2 py-2 w-64">Arbitrage</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => {
                  const isProc = processing[s.sale_id];
                  return (
                    <tr key={s.sale_id} className="border-t border-[#E5E5E0] hover:bg-[#FAFAF7]" data-testid={`review-row-${s.external_id}`}>
                      <td className="px-2 py-2 font-mono text-[10px]">
                        {s.sold_at ? new Date(s.sold_at).toLocaleString("fr-CH", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "?"}
                      </td>
                      <td className="px-2 py-2">{s.distributor_name}</td>
                      <td className="px-2 py-2">
                        <div>{s.product_name}</div>
                        <div className="text-[10px] text-[#6B6E6B] font-mono">
                          {s.lot_number || <span className="italic">— orphan</span>}
                          {s.slot_number != null && <span> · slot {s.slot_number}</span>}
                          {s.lot_qty_now != null && <span> · qty actuelle {s.lot_qty_now}</span>}
                        </div>
                      </td>
                      <td className="px-2 py-2 text-center">{s.quantity}</td>
                      <td className="px-2 py-2 text-right font-mono">
                        {(s.unit_price || 0).toFixed(2)}
                        {s.product_ref_price && s.unit_price && Math.abs(s.unit_price - s.product_ref_price) / s.product_ref_price > 0.2 && (
                          <div className="text-[9px] text-red-600">réf AM {s.product_ref_price.toFixed(2)}</div>
                        )}
                      </td>
                      <td className="px-2 py-2">{reasonLabel(s.review_reason)}</td>
                      <td className="px-2 py-2 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="sm" variant="outline"
                            onClick={() => arbitrate(s, "KEEP")}
                            disabled={!!isProc}
                            className="h-7 text-[10px] bg-green-50 hover:bg-green-100 border-green-300"
                            data-testid={`review-keep-${s.external_id}`}
                          >
                            {isProc === "KEEP" ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                            Garder
                          </Button>
                          <Button
                            size="sm" variant="outline"
                            onClick={() => arbitrate(s, "MARK_CANCELED")}
                            disabled={!!isProc}
                            className="h-7 text-[10px] bg-orange-50 hover:bg-orange-100 border-orange-300"
                            data-testid={`review-cancel-${s.external_id}`}
                          >
                            {isProc === "MARK_CANCELED" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Archive className="w-3 h-3 mr-1" />}
                            Annuler
                          </Button>
                          <Button
                            size="sm" variant="destructive"
                            onClick={() => arbitrate(s, "DELETE")}
                            disabled={!!isProc}
                            className="h-7 text-[10px]"
                            data-testid={`review-delete-${s.external_id}`}
                          >
                            {isProc === "DELETE" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
                            Supprimer
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
